import { useCallback, useEffect, useMemo, useRef, useState } from "react"

const autoSlideDelay = 3000
const slideDuration = 650

const defaultFeatures = [
  {
    title: "Embedded eSIM",
    desc: "Devices with embedded eSIM that poll on dual servers and transmit data to government's control central server and service provider.",
  },
  {
    title: "Embedded eSIM",
    desc: "Devices with embedded eSIM that poll on dual servers and transmit data to government's control central server and service provider.",
  },
  {
    title: "Embedded eSIM",
    desc: "Devices with embedded eSIM that poll on dual servers and transmit data to government's control central server and service provider.",
  },
]

const featureIcons = [
  (
    <path
      d="M12 3v18M6.5 6.5h11M6.5 17.5h11M5 12h14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  ),
  (
    <>
      <path
        d="M4 13.5 9 18l11-12"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 20h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </>
  ),
  (
    <>
      <path
        d="M12 3 4.5 6.5v5.7c0 4.4 3 7.2 7.5 8.8 4.5-1.6 7.5-4.4 7.5-8.8V6.5L12 3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="m9.5 12 1.8 1.8 3.7-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  (
    <>
      <path d="M5 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M10 17v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M15 17V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M20 17V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  (
    <>
      <path
        d="M12 7v5l3 2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 12a9 9 0 1 1-2.64-6.36"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M21 4v5h-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  (
    <>
      <path
        d="M12 21c4-3.5 7-7.2 7-11.3A7 7 0 0 0 5 9.7C5 13.8 8 17.5 12 21Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 10.5c1.8.2 3.6-.5 5-2.2.1 2.3-.8 4.2-2.4 5.4-1.3 1-2.8 1.2-4.1.7.2-1.5.7-2.8 1.5-3.9Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </>
  ),
]

function FeatureIcon({ index = 0, isCenter = false }) {
  return (
    <div
      className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${
        isCenter
          ? "bg-[#ed860f]/15 text-[#ed860f]"
          : "bg-white/90 text-[#ed860f]"
      }`}
    >
      <svg viewBox="0 0 24 24" className="h-9 w-9" fill="none" aria-hidden="true">
        {featureIcons[index % featureIcons.length]}
      </svg>
    </div>
  )
}

function FeatureCard({ item, index, isCenter }) {
  return (
    <div
      className={`key-benefits-card flex min-h-[340px] flex-col rounded-xl p-6 transition-colors duration-300 sm:p-7 ${
        isCenter ? "bg-black text-white" : "bg-[#ed860f] text-black"
      }`}
    >
      <FeatureIcon index={index} isCenter={isCenter} />

      <h3 className="mb-4 text-lg font-bold sm:text-xl">{item.title}</h3>

      <p
        className={`text-[13px] leading-relaxed sm:text-sm ${
          isCenter ? "text-gray-100" : "text-black"
        }`}
      >
        {item.desc}
      </p>
    </div>
  )
}

export default function KeyFeatures({
  title = "Key Features",
  features = defaultFeatures,
}) {
  const safeFeatures = features.length ? features : defaultFeatures

  const [activeFeature, setActiveFeature] = useState(0)
  const [slideDirection, setSlideDirection] = useState("next")
  const [isAnimating, setIsAnimating] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  const timeoutRef = useRef(null)

  const getIndex = useCallback(
    (index) => {
      return (index + safeFeatures.length) % safeFeatures.length
    },
    [safeFeatures.length]
  )

  const finishSlide = useCallback(
    (direction) => {
      setActiveFeature((prev) => {
        if (direction === "next") return getIndex(prev + 1)
        return getIndex(prev - 1)
      })

      setIsAnimating(false)
    },
    [getIndex]
  )

  const startSlide = useCallback(
    (direction) => {
      if (isAnimating || safeFeatures.length <= 1) return

      setSlideDirection(direction)
      setIsAnimating(true)

      window.clearTimeout(timeoutRef.current)

      timeoutRef.current = window.setTimeout(() => {
        finishSlide(direction)
      }, slideDuration)
    },
    [finishSlide, isAnimating, safeFeatures.length]
  )

  useEffect(() => {
    if (isPaused || isAnimating || safeFeatures.length <= 1) return undefined

    const interval = window.setInterval(() => {
      startSlide("next")
    }, autoSlideDelay)

    return () => window.clearInterval(interval)
  }, [isPaused, isAnimating, safeFeatures.length, startSlide])

  useEffect(() => {
    return () => window.clearTimeout(timeoutRef.current)
  }, [])

  const renderOffsets = useMemo(() => {
    if (!isAnimating) return [-1, 0, 1]

    return slideDirection === "next" ? [-1, 0, 1, 2] : [-2, -1, 0, 1]
  }, [isAnimating, slideDirection])

  const visualCenterOffset = isAnimating
    ? slideDirection === "next"
      ? 1
      : -1
    : 0

  const progressIndex = isAnimating
    ? slideDirection === "next"
      ? getIndex(activeFeature + 1)
      : getIndex(activeFeature - 1)
    : activeFeature

  const containerClass =
    "mx-auto w-full max-w-[1600px] px-5 sm:px-8 lg:px-12 xl:px-20"

  return (
    <section
      className="overflow-hidden bg-white py-14 sm:py-16 lg:py-20"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className={containerClass}>
        <h2 className="mb-8 text-center text-3xl font-bold text-black sm:text-4xl lg:text-[42px]">
          {title}
        </h2>

        <div className="relative mx-auto max-w-[1350px]">
          <button
            type="button"
            disabled={isAnimating}
            onClick={() => startSlide("prev")}
            className="absolute left-0 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#ed860f] text-2xl font-bold text-white shadow-md transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 sm:h-12 sm:w-12 sm:text-3xl lg:-left-6"
            aria-label="Previous feature"
          >
            ‹
          </button>

          <div className="key-benefits-viewport mx-auto">
            <div
              className={`key-benefits-track ${
                isAnimating
                  ? slideDirection === "next"
                    ? "key-benefits-track-next"
                    : "key-benefits-track-prev"
                  : ""
              }`}
            >
              {renderOffsets.map((offset) => {
                const originalIndex = getIndex(activeFeature + offset)
                const item = safeFeatures[originalIndex]

                return (
                  <FeatureCard
                    key={`${originalIndex}-${offset}-${isAnimating ? slideDirection : "idle"}`}
                    item={item}
                    index={originalIndex}
                    isCenter={offset === visualCenterOffset}
                  />
                )
              })}
            </div>
          </div>

          <button
            type="button"
            disabled={isAnimating}
            onClick={() => startSlide("next")}
            className="absolute right-0 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-[#ed860f] text-2xl font-bold text-white shadow-md transition hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60 sm:h-12 sm:w-12 sm:text-3xl lg:-right-6"
            aria-label="Next feature"
          >
            ›
          </button>
        </div>

        <div className="mx-auto mt-6 h-1.5 w-[220px] overflow-hidden rounded-full bg-gray-200 sm:w-[260px]">
          <div
            className="h-full rounded-full bg-[#ed860f] transition-all duration-500 ease-out"
            style={{
              width: `${((progressIndex + 1) / safeFeatures.length) * 100}%`,
            }}
          />
        </div>
      </div>
    </section>
  )
}